import React, { useState, useEffect } from "react";
import { downloadQuotation, updateSelection } from "../../../services/api";
import InquiryModal from "../InquiryModal";
import { showErrorToast, showSuccessToast } from "../../../services/toast";
import { useNavigate } from "react-router-dom";

const formatDecimal = (val) =>
  val !== undefined && val !== null && !isNaN(val)
    ? Number(val).toFixed(3)
    : "0.000";

export default function QuotationTable({
  quotation,
  selectionId,
  visualizerToken,
  onDataChange,
}) {
  const navigate = useNavigate();
  const [quotationGenerated, setQuotationGenerated] = useState(false);
  const [wastage, setWastage] = useState(quotation?.wastage_percentage || 10);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);

  // Sync state when quotation prop updates
  useEffect(() => {
    if (quotation?.items) {
      setItems(
        quotation.items.map((item) => ({
          ...item,
          // Track input choice and active value for editing
          input_type: item.input_type || "sq_ft",
          input_value: item.input_value ?? item.area_sqft ?? 0,
        })),
      );
    }
  }, [quotation]);

  // Handle single cell input edits
  const handleInputChange = (index, field, value) => {
    setItems((prevItems) => {
      const updated = [...prevItems];
      const targetItem = { ...updated[index] };

      targetItem[field] = value;

      if (field === "area_sqm") {
        targetItem.input_type = "sq_mt";
        targetItem.input_value = value;
      }

      if (field === "area_sqft") {
        targetItem.input_type = "sq_ft";
        targetItem.input_value = value;
      }

      if (field === "boxes_needed") {
        targetItem.input_type = "box";
        targetItem.input_value = value;
      }

      updated[index] = targetItem;

      return updated;
    });
  };

  // Triggered when clicking "Generate Quotation"
  const handleGenerateQuotation = async () => {
    if (!selectionId) {
      showErrorToast("Selection ID missing.");
      return;
    }

    try {
      setLoading(true);

      const updatePayload = {
        wastage_percentage: Number(wastage),
        items: items.map((item) => ({
          id: item.id,
          input_type: item.input_type,
          input_value: Number(item.input_value || 0),
        })),
      };

      const updatedData = await updateSelection(selectionId, updatePayload);

      if (updatedData?.items) {
        setItems(
          updatedData.items.map((item) => ({
            ...item,
            input_type: item.input_type || "sq_ft",
            input_value: item.input_value ?? item.area_sqft ?? 0,
          }))
        );
      }

      if (onDataChange) {
        onDataChange();
      }

      showSuccessToast("Quotation generated successfully!");

      // Navigate to the preview/download page passing selectionId
      navigate(`/quotation-preview/${selectionId}`); 
      // Or in Next.js: router.push(`/quotation-preview/${selectionId}`);

    } catch (error) {
      console.error("Quotation update error:", error);
      showErrorToast(error?.message || error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQuotation = async () => {
    if (!selectionId) {
      showErrorToast("Selection ID missing.");
      return;
    }

    try {
      setLoading(true);

      await downloadQuotation(selectionId);

      showSuccessToast("Quotation downloaded successfully!");
    } catch (error) {
      console.error("Quotation download error:", error);
      showErrorToast(error);
    } finally {
      setLoading(false);
    }
  };

  // Compute local display totals
  const totalAreaSqft = items.reduce(
    (acc, i) => acc + (Number(i.area_sqft) || 0),
    0,
  );
  const totalAreaSqm = items.reduce(
    (acc, i) => acc + (Number(i.area_sqm) || 0),
    0,
  );
  const totalBoxes = items.reduce(
    (acc, i) => acc + (Number(i.boxes_needed) || 0),
    0,
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Quotation</h2>

      {/* Header Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 bg-gray-50 border border-gray-200 rounded-lg p-3 text-center mb-6 gap-y-3">
        <div className="border-r border-gray-200 last:border-r-0">
          <div className="text-base font-bold text-gray-900">
            {formatDecimal(totalAreaSqft)}
          </div>
          <div className="text-xs text-gray-500 font-medium">Area sqft</div>
        </div>
        <div className="border-r border-gray-200 last:border-r-0">
          <div className="text-base font-bold text-gray-900">
            {formatDecimal(totalAreaSqm)}
          </div>
          <div className="text-xs text-gray-500 font-medium">Area sqmt</div>
        </div>
        <div className="border-r border-gray-200 last:border-r-0">
          <div className="text-base font-bold text-gray-900">{totalBoxes}</div>
          <div className="text-xs text-gray-500 font-medium">Boxes</div>
        </div>
        <div className="border-r border-gray-200 last:border-r-0">
          <div className="text-base font-bold text-gray-900">
            {quotation?.total_weight
              ? `${quotation.total_weight} Kg`
              : "NaN Kg"}
          </div>
          <div className="text-xs text-gray-500 font-medium">Weight</div>
        </div>
        <div>
          <div className="text-base font-bold text-gray-900">
            {quotation?.total_tonne ?? "NaN"}
          </div>
          <div className="text-xs text-gray-500 font-medium">Tonne</div>
        </div>
      </div>

      {/* Main Calculations Table */}
      <div className="border border-gray-200 rounded-lg overflow-x-auto mb-5">
        <table className="w-full text-xs text-left border-collapse">
          <thead className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-200">
            <tr>
              <th className="p-2.5">Sr. No</th>
              <th className="p-2.5">Area</th>
              <th className="p-2.5">Product</th>
              <th className="p-2.5">Size</th>
              <th className="p-2.5">Application</th>
              <th className="p-2.5 text-center">pieces/Box</th>
              <th className="p-2.5 text-center">Thickness</th>
              <th className="p-2.5">Area/Box (sqft)</th>
              <th className="p-2.5 text-center min-w-[140px]">
                Required Area (sqmt | sqft | Box)
              </th>
              <th className="p-2.5 text-center">Required Box</th>
              <th className="p-2.5 text-center">Price/Box</th>
              <th className="p-2.5 text-center">Price/sqm</th>
              <th className="p-2.5 text-center">Total Price</th>
              <th className="p-2.5 text-center">Weight (Kg)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 text-gray-800">
            {items.map((item, idx) => (
              <tr key={item.id || idx} className="hover:bg-gray-50/50">
                <td className="p-2.5">{idx + 1}</td>
                <td className="p-2.5 font-medium">{item.region}</td>
                <td className="p-2.5 font-medium uppercase">
                  {item.product_name || item.product_sku || "-"}
                </td>
                <td className="p-2.5 whitespace-nowrap">
                  {item.product_size || "-"}
                </td>
                <td className="p-2.5">{item.application_type || "Wall"}</td>
                <td className="p-2.5 text-center">
                  {item.pieces_per_box || "-"}
                </td>
                <td className="p-2.5 text-center">{item.thickness || "-"}</td>
                <td className="p-2.5 text-center">
                  {formatDecimal(item.coverage_area_per_box)}
                </td>
                <td className="p-2.5">
                  <div className="space-y-1">
                    <div className="grid grid-cols-2 gap-1">
                      <input
                        type="number"
                        placeholder="sqmt"
                        value={item.area_sqm ?? ""}
                        onChange={(e) =>
                          handleInputChange(idx, "area_sqm", e.target.value)
                        }
                        className="border border-gray-300 rounded px-1.5 py-0.5 text-center text-xs w-full focus:ring-1 focus:ring-gray-400"
                      />
                      <input
                        type="number"
                        placeholder="sqft"
                        value={item.area_sqft ?? ""}
                        onChange={(e) =>
                          handleInputChange(idx, "area_sqft", e.target.value)
                        }
                        className="border border-gray-300 rounded px-1.5 py-0.5 text-center text-xs w-full focus:ring-1 focus:ring-gray-400"
                      />
                    </div>
                    <input
                      type="number"
                      placeholder="Boxes"
                      value={item.boxes_needed ?? ""}
                      onChange={(e) =>
                        handleInputChange(idx, "boxes_needed", e.target.value)
                      }
                      className="border border-gray-300 rounded px-1.5 py-0.5 text-center text-xs w-full focus:ring-1 focus:ring-gray-400"
                    />
                  </div>
                </td>
                <td className="p-2.5 text-center font-medium">
                  {item.boxes_needed ?? 0}
                </td>
                <td className="p-2.5 text-center font-medium">
                  {item.weight_kg ?? "0.00"}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-gray-50 border-t border-gray-200 font-semibold text-gray-900">
            <tr>
              <td colSpan={8} className="p-3 text-left">
                Total
              </td>
              <td className="p-3 text-center text-xs">
                <div>sqft: {formatDecimal(totalAreaSqft)}</div>
                <div>sqmt: {formatDecimal(totalAreaSqm)}</div>
              </td>
              <td className="p-3 text-center">{totalBoxes}</td>
              <td className="p-3 text-center">
                {quotation?.total_weight ?? "NaN"}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Footer Options & Action Buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-3">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="waste"
            defaultChecked
            className="rounded border-gray-300 text-gray-900 focus:ring-gray-900 w-4 h-4"
          />
          <label
            htmlFor="waste"
            className="text-xs font-semibold text-gray-700 flex items-center gap-1"
          >
            Add waste and reserve material (in sqft)
            <span className="cursor-pointer text-gray-400">ⓘ</span>
          </label>

          <select
            value={wastage}
            onChange={(e) => setWastage(Number(e.target.value))}
            className="border border-gray-300 rounded-md text-xs py-1 px-2 bg-white text-gray-700 font-medium focus:outline-none focus:ring-1 focus:ring-gray-500"
          >
            <option value={5}>5%</option>
            <option value={10}>10%</option>
            <option value={15}>15%</option>
            <option value={20}>20%</option>
          </select>
        </div>

        <div className="flex items-center gap-3">
          

<button
        onClick={handleGenerateQuotation}
        disabled={loading}
        className="px-4 py-2 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        {loading ? "Generating..." : "Generate Quotation"}
      </button>

          <button
            onClick={() => setShowInquiryModal(true)}
            className="px-4 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-black flex items-center gap-1 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            Send Inquiry
          </button>

          <InquiryModal
            isOpen={showInquiryModal}
            onClose={() => setShowInquiryModal(false)}
            visualizerToken={visualizerToken}
            quotationId={selectionId}
            items={items}
            totals={quotation}
          />

          {/* <button className="px-5 py-2 bg-gray-900 text-white rounded-lg text-xs font-semibold hover:bg-black flex items-center gap-1.5 transition-colors">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
              />
            </svg>
            Share
          </button> */}
        </div>
      </div>
    </div>
  );
}
